/**
 * Chatbot API client for Platform Assistant.
 * Provides functions for chatbot session management and messaging.
 */
import { apiOrigin } from '@/lib/config'
import { ApiError } from './client'
import type {
  ChatbotSession,
  ChatbotMessage,
  CreateChatbotSessionRequest,
  CreateChatbotSessionResponse,
  ListChatbotSessionsResponse,
  AddContextItemRequest,
  AddContextItemResponse,
  RemoveContextItemRequest,
} from '@/types/chatbot'
import { apiFetch } from './http'

/**
 * Get the orchestrator API base URL from config
 * Ensures trailing slash for correct URL resolution with new URL()
 */
function getOrchestratorUrl(): string {
  // Absolute, because the callers below build URLs with `new URL(path, base)` and a relative base throws.
  // Behind the BFF the configured origin is empty, which means *this* origin — same-origin is the point
  // (checklist D5), and an absolute URL to the page's own origin is still same-origin.
  return `${apiOrigin() || window.location.origin}/`
}

/**
 * Create a new chatbot session.
 */
export async function createChatbotSession(
  request?: CreateChatbotSessionRequest
): Promise<CreateChatbotSessionResponse> {
  const baseUrl = getOrchestratorUrl()

  const response = await apiFetch(`${baseUrl}api/chatbot/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request || {}),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * List chatbot sessions for the current user.
 */
export async function listChatbotSessions(
  params?: { limit?: number; offset?: number }
): Promise<ListChatbotSessionsResponse> {
  const baseUrl = getOrchestratorUrl()
  const url = new URL('api/chatbot/sessions', baseUrl)

  if (params?.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit))
  }
  if (params?.offset !== undefined) {
    url.searchParams.set('offset', String(params.offset))
  }

  const response = await apiFetch(url.toString(), {
    method: 'GET',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Get a specific chatbot session by ID.
 */
export async function getChatbotSession(sessionId: string): Promise<ChatbotSession> {
  const baseUrl = getOrchestratorUrl()

  const response = await apiFetch(`${baseUrl}api/chatbot/sessions/${sessionId}`, {
    method: 'GET',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Delete a chatbot session.
 */
export async function deleteChatbotSession(sessionId: string): Promise<void> {
  const baseUrl = getOrchestratorUrl()

  const response = await apiFetch(`${baseUrl}api/chatbot/sessions/${sessionId}`, {
    method: 'DELETE',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}

/**
 * Update a chatbot session's project association.
 */
export async function updateSessionProject(
  sessionId: string,
  projectId: string | null
): Promise<{ success: boolean; projectId?: string; projectName?: string }> {
  const baseUrl = getOrchestratorUrl()

  const response = await apiFetch(`${baseUrl}api/chatbot/sessions/${sessionId}/project`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ projectId }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Transform raw history messages to match real-time display format.
 * - Converts 'tool' role messages to assistant role for consistent display
 * Note: Backend now stores tool names for assistant tool_use blocks (no more "[no content]")
 */
function transformHistoryMessages(messages: ChatbotMessage[]): ChatbotMessage[] {
  return messages.map(msg => {
    // Convert 'tool' role to 'assistant' role for consistent display
    // The content is the tool result which should be displayed
    if ((msg.role as string) === 'tool') {
      return {
        ...msg,
        role: 'assistant',
      }
    }
    
    return msg
  })
}

/**
 * Pagination options for chat history.
 */
export interface ChatHistoryPaginationOptions {
  /** Maximum number of messages to return. Default is 20. */
  limit?: number
  /** Number of messages to skip from the end (for loading older messages). */
  offset?: number
}

/**
 * Response from paginated chat history.
 */
export interface ChatHistoryResponse {
  messages: ChatbotMessage[]
  totalCount: number
  hasMore: boolean
}

/**
 * Get conversation history for a chatbot session.
 * Supports pagination - loads most recent messages first.
 */
export async function getChatbotHistory(
  sessionId: string, 
  options?: ChatHistoryPaginationOptions
): Promise<ChatHistoryResponse> {
  const baseUrl = getOrchestratorUrl()

  const params = new URLSearchParams()
  if (options?.limit !== undefined) params.set('limit', options.limit.toString())
  if (options?.offset !== undefined) params.set('offset', options.offset.toString())
  
  const queryString = params.toString()
  const url = `${baseUrl}api/chatbot/sessions/${sessionId}/history${queryString ? `?${queryString}` : ''}`

  const response = await apiFetch(url, {
    method: 'GET',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  const data = await response.json()
  
  // Transform messages to match real-time display format
  return {
    messages: transformHistoryMessages(data.messages),
    totalCount: data.totalCount ?? data.messages.length,
    hasMore: data.hasMore ?? false
  }
}

/**
 * Add a context item to a chatbot session.
 * Context items provide additional context for the AI assistant.
 */
export async function addContextItem(
  sessionId: string,
  request: AddContextItemRequest
): Promise<AddContextItemResponse> {
  const baseUrl = getOrchestratorUrl()

  const response = await apiFetch(`${baseUrl}api/chatbot/sessions/${sessionId}/context`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionId,
      type: request.type,
      value: request.value,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }

  return response.json()
}

/**
 * Remove a context item from a chatbot session.
 */
export async function removeContextItem(
  sessionId: string,
  request: RemoveContextItemRequest
): Promise<void> {
  const baseUrl = getOrchestratorUrl()

  const response = await apiFetch(`${baseUrl}api/chatbot/sessions/${sessionId}/context`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      sessionId,
      type: request.type,
      value: request.value,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new ApiError(
      errorData.error || `HTTP error ${response.status}`,
      response.status,
      response.statusText,
      errorData
    )
  }
}
